import React from 'react';
import type { ColumnDef } from '@tanstack/react-table';

import { FlagIcon } from './FlagIcon';
import { FlagToggleButton } from './FlagToggleButton';
import { useFlagScopeTexts } from '../../../../hooks/useFlagScopeTexts';

export const FLAG_COLUMN_ID = 'flag';

/** Centra el contenido de la celda para que cabecera y botón compartan eje. */
const centeredStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
};

/** Texto solo para lectores de pantalla (técnica clásica sr-only). */
const visuallyHiddenStyle: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  margin: -1,
  padding: 0,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

/**
 * Atributo que hace que los exportadores de Excel/PDF (`table-to-excel`,
 * `table-to-pdf`) descarten la celda antes de aplicar ningún índice de columna.
 */
export function exportIgnoreProps(columnId: string): {
  'data-export-ignore'?: 'true';
} {
  return columnId === FLAG_COLUMN_ID ? { 'data-export-ignore': 'true' } : {};
}

/** Cabecera: el icono no cambia; título y texto sr-only dicen el ámbito. */
const FlagHeader = (): JSX.Element => {
  const { header } = useFlagScopeTexts();
  return (
    <span style={centeredStyle} title={header}>
      <FlagIcon filled={false} />
      <span style={visuallyHiddenStyle}>{header}</span>
    </span>
  );
};

/**
 * Columna de la bandera, justo detrás de la de selección. Solo las tablas que
 * reciben `showFlagColumn` la añaden.
 */
export function createFlagColumn<T>(
  getOrderId: (row: T) => string | undefined
): ColumnDef<T, unknown> {
  return {
    id: FLAG_COLUMN_ID,
    enableHiding: false,
    enableSorting: false,
    size: 56,
    minSize: 56,
    // Icono centrado; el texto queda como nombre accesible y tooltip.
    header: () => <FlagHeader />,
    cell: ({ row }) => {
      const orderId = getOrderId(row.original);
      return orderId ? (
        <div style={centeredStyle}>
          <FlagToggleButton orderId={orderId} />
        </div>
      ) : null;
    },
  };
}
